/**
 * MODO ESTADIO — "pide desde tu asiento" (demo 13-oct-2026).
 *
 * Todo lo de este archivo (y los componentes `Stadium*`) sólo corre cuando el
 * modo está activo para el negocio. Sin el modo, el guest se ve y funciona
 * exactamente igual que antes para el resto de los negocios.
 *
 * Cómo se activa (por negocio, sin migraciones):
 *  1. URL: `?modo=estadio` en cualquier liga del negocio (el portal del estadio
 *     la agrega a cada QR). Se recuerda para ESE slug en este navegador por
 *     `STADIUM_TTL_MS` (12 h) para que el seguimiento siga en modo estadio
 *     aunque la liga ya no traiga el parámetro. `?modo=normal` lo apaga.
 *  2. Build: `VITE_STADIUM_SLUGS="pizzas-estadio,burgers-estadio"` fuerza el
 *     modo para esos slugs sin parámetro (vacío por defecto).
 */
import { createContext, useContext } from 'react'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { OrderDto, OrderItemDto } from './api.js'

export const STADIUM_PARAM = 'modo'
export const STADIUM_VALUE = 'estadio'
export const STADIUM_TTL_MS = 12 * 60 * 60 * 1000
const STORAGE_PREFIX = 'ntg-modo-estadio:'

/**
 * PAGO DE DEMOSTRACIÓN — el modo estadio NO usa pasarela. "Pagar ahora" simula
 * el proceso (`DEMO_PAYMENT_DELAY_MS`) y llama a `POST /orders/:id/demo-payment`,
 * que deja el pedido pagado como un prepago real (ver
 * services/pos-orders/src/lib/demo-payment.ts). En pantalla dice "Pago de
 * demostración". Para cobrar de verdad hay que conectar la pasarela del negocio.
 */
export const DEMO_PAYMENT = true as const
export const DEMO_PAYMENT_DELAY_MS = 1500

/** Slugs con el modo forzado desde el build (opcional). */
export function buildTimeStadiumSlugs(raw: string | undefined = import.meta.env?.VITE_STADIUM_SLUGS): Set<string> {
  return new Set(
    (raw ?? '')
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  )
}

/** Lee el parámetro de la URL: 'on' | 'off' | null (sin parámetro). */
export function parseStadiumParam(search: string): 'on' | 'off' | null {
  const v = new URLSearchParams(search).get(STADIUM_PARAM)
  if (v === null) return null
  return v.trim().toLowerCase() === STADIUM_VALUE ? 'on' : 'off'
}

interface StorageLike {
  getItem(k: string): string | null
  setItem(k: string, v: string): void
  removeItem(k: string): void
}

function safeStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

/**
 * Resuelve (y recuerda) si el modo estadio aplica a `slug`. Puro respecto a sus
 * argumentos para poder probarlo sin navegador.
 */
export function resolveStadiumMode(
  slug: string | null | undefined,
  search: string,
  storage: StorageLike | null = safeStorage(),
  now: number = Date.now(),
  forced: Set<string> = buildTimeStadiumSlugs(),
): boolean {
  if (!slug) return false
  const key = STORAGE_PREFIX + slug.toLowerCase()
  const param = parseStadiumParam(search)
  try {
    if (param === 'on') {
      storage?.setItem(key, String(now))
      return true
    }
    if (param === 'off') {
      storage?.removeItem(key)
      return false
    }
  } catch {
    /* almacenamiento bloqueado: el parámetro manda en esta carga */
    return param === 'on'
  }
  if (forced.has(slug.toLowerCase())) return true
  try {
    const at = Number(storage?.getItem(key) ?? '')
    if (Number.isFinite(at) && at > 0 && now - at < STADIUM_TTL_MS) return true
    if (at) storage?.removeItem(key)
  } catch {
    /* sin almacenamiento → sin recuerdo */
  }
  return false
}

/** Slug de la ruta actual (`/r/:slug/...`). */
export function slugFromPath(pathname: string): string | null {
  const m = /^\/r\/([^/?#]+)/.exec(pathname)
  return m ? decodeURIComponent(m[1]!) : null
}

/** Marca el <html> con `data-modo="estadio"`: el CSS del modo cuelga de ahí. */
export function applyStadiumMode(on: boolean): void {
  if (typeof document === 'undefined') return
  const el = document.documentElement
  if (on) el.dataset['modo'] = STADIUM_VALUE
  else delete el.dataset['modo']
}

/** El valor lo pone App (por ruta + URL); los componentes lo leen. */
export const StadiumContext = createContext<boolean>(false)

/** ¿Estamos en modo estadio? (false fuera del provider = comportamiento normal) */
export function useStadiumMode(): boolean {
  return useContext(StadiumContext)
}

// ── Ubicación (Sección / Fila / Asiento) ────────────────────────────────────

export interface SeatLocation {
  section: string
  row: string
  seat: string
  /** Nombre opcional (lo ve quien entrega en "Este pedido es de"). */
  name?: string
}

export function cleanSeatPart(v: string): string {
  return v.replace(/[|\n\r]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 12)
}

export function isSeatComplete(s: SeatLocation | null | undefined): s is SeatLocation {
  return !!s && !!cleanSeatPart(s.section) && !!cleanSeatPart(s.row) && !!cleanSeatPart(s.seat)
}

/** "Sec 104, Fila 12, Asiento 8" — sin emojis ni símbolos raros: la comanda
 *  térmica no los imprime. */
export function formatSeat(s: SeatLocation): string {
  return `Sec ${cleanSeatPart(s.section)}, Fila ${cleanSeatPart(s.row)}, Asiento ${cleanSeatPart(s.seat)}`
}

/** Prefijo que el POS reconoce (PickupDeliveryPanel) para mostrar la ubicación. */
export const DELIVER_TO_PREFIX = 'Entregar en '

/**
 * Nota del ítem con la ubicación. Va en CADA ítem para que cada estación del KDS
 * y cada comanda la lleve; el POS la muestra una sola vez al entregar. Respeta la
 * nota del comensal ("sin cebolla") y el tope de 500 del backend.
 */
export function itemNotesWithSeat(userNotes: string | undefined, seat: SeatLocation): string {
  const where = DELIVER_TO_PREFIX + formatSeat(seat)
  const base = (userNotes ?? '').trim()
  const joined = base ? `${base} | ${where}` : where
  return joined.slice(0, 500)
}

interface SeatState {
  /** Ubicación por slug (cada negocio del estadio tiene su carrito y su pedido). */
  bySlug: Record<string, SeatLocation>
  setSeat: (slug: string, s: SeatLocation) => void
}

export const useSeatStore = create<SeatState>()(
  persist(
    (set, get) => ({
      bySlug: {},
      setSeat: (slug, s) => set({ bySlug: { ...get().bySlug, [slug]: s } }),
    }),
    { name: 'ntg-estadio-asiento' },
  ),
)

// ── Seguimiento: pasos del pedido ───────────────────────────────────────────

export type StadiumStep = 'received' | 'preparing' | 'ready' | 'on_the_way' | 'delivered'

export const STADIUM_STEPS: { key: StadiumStep; label: string }[] = [
  { key: 'received', label: 'Recibido' },
  { key: 'preparing', label: 'Preparando' },
  { key: 'ready', label: 'Listo' },
  { key: 'on_the_way', label: 'En camino' },
  { key: 'delivered', label: 'Entregado' },
]

export type StadiumPhase =
  | { kind: 'cancelled' }
  | { kind: 'rejected' }
  | { kind: 'awaiting_payment' }
  | { kind: 'step'; step: StadiumStep }

/**
 * Paso visible a partir de los estados REALES (sin estados nuevos):
 *  - Entregado  = `order.deliveredAt` (lo pone el POS al validar el código).
 *  - En camino  = cocina despachó (bump del KDS → todos los ítems DELIVERED)
 *                 pero aún no se valida la entrega con el código.
 *  - Listo      = todos los ítems READY en el KDS.
 *  - Preparando = algún ítem ya en cocina (SENT / PREPARING).
 *  - Recibido   = pagado/aceptado, todavía sin entrar a cocina.
 */
export function stadiumPhase(order: Pick<OrderDto, 'status' | 'deliveredAt' | 'totalCents' | 'paidCents' | 'type'> & { items: Pick<OrderItemDto, 'status'>[] }): StadiumPhase {
  if (order.status === 'CANCELLED') return { kind: 'cancelled' }
  if (order.status === 'REJECTED') return { kind: 'rejected' }
  if (order.deliveredAt) return { kind: 'step', step: 'delivered' }
  const live = order.items.filter((i) => i.status !== 'CANCELLED')
  const paid = order.totalCents > 0 && (order.paidCents ?? 0) >= order.totalCents
  if (live.length > 0 && live.every((i) => i.status === 'DELIVERED')) return { kind: 'step', step: 'on_the_way' }
  if (live.length > 0 && live.every((i) => i.status === 'READY' || i.status === 'DELIVERED')) return { kind: 'step', step: 'ready' }
  if (live.some((i) => i.status === 'SENT' || i.status === 'PREPARING' || i.status === 'READY')) return { kind: 'step', step: 'preparing' }
  if (!paid && order.type === 'TAKEOUT') return { kind: 'awaiting_payment' }
  return { kind: 'step', step: 'received' }
}

/** Minutos estimados por paso (referencia para la demo, no un SLA). */
const ETA_BY_STEP: Record<StadiumStep, number> = {
  received: 15,
  preparing: 10,
  ready: 6,
  on_the_way: 3,
  delivered: 0,
}

export function stadiumEtaMinutes(step: StadiumStep): number {
  return ETA_BY_STEP[step]
}

export function stepIndex(step: StadiumStep): number {
  return STADIUM_STEPS.findIndex((s) => s.key === step)
}
